import { HOMEPAGE_ANALYTICS } from 'constants/productAnalytics';

// Tracks whether the current page was the first one the signed-in user saw
// after Amplitude was ready ("landing") or one they moved to ("navigation"),
// and emits HOMEPAGE_CANDIDATE_VIEWED when the user enters one of the pages
// that can be picked as homepage. See docs/project/HomeAnalyticsEvents.md.

export type TPageEntry = 'landing' | 'navigation';

export type TPageFamily =
  | 'home'
  | 'meetings'
  | 'conversations'
  | 'deals'
  | 'scored_conversations'
  | 'scorecard_requests'
  | 'forecast'
  | 'calls';

// Each family is a list view root from APP_HOMEPAGE_ROUTES (plus Home). A path
// matches when it is the root or below it, so detail routes such as
// /meetings/<uuid> never match.
const PAGE_FAMILY_ROOTS: ReadonlyArray<[TPageFamily, string]> = [
  ['home', '/home/views'],
  ['meetings', '/meetings/views'],
  ['conversations', '/dashboard/interaction'],
  ['deals', '/revenue/deals/views'],
  ['scored_conversations', '/coaching/scored-conversations'],
  ['scorecard_requests', '/coaching/requests'],
  ['forecast', '/revenue/forecast'],
  ['calls', '/calls/views']
];

export const getPageFamily = (pathname: string): TPageFamily | null => {
  if (pathname === '/home' || pathname === '/home/') {
    return 'home';
  }

  const match = PAGE_FAMILY_ROOTS.find(
    ([, root]) => pathname === root || pathname.startsWith(`${root}/`)
  );

  return match ? match[0] : null;
};

type TTrackEvent = (eventName: string, properties: object) => void;

interface IPendingCandidateView {
  page: TPageFamily;
  entry: TPageEntry;
}

// Set by markAnalyticsReady. Passed in rather than imported so this module
// does not form an import cycle with helpers/amplitude.
let trackEvent: TTrackEvent | null = null;
let hasNavigated = false;
let lastPathname: string | null = null;
// `undefined` until the user's default homepage is known; candidate views are
// held until then so each one can say whether it is the default homepage.
let defaultHomepageFamily: TPageFamily | null | undefined;
let pendingCandidateViews: IPendingCandidateView[] = [];

export const getPageEntry = (): TPageEntry =>
  hasNavigated ? 'navigation' : 'landing';

const emitCandidateView = ({ page, entry }: IPendingCandidateView) => {
  trackEvent?.(HOMEPAGE_ANALYTICS.candidateViewed, {
    page,
    entry,
    is_default_homepage: page === defaultHomepageFamily
  });
};

const recordCandidateView = (page: TPageFamily) => {
  const view = { page, entry: getPageEntry() };

  if (defaultHomepageFamily === undefined) {
    pendingCandidateViews.push(view);
    return;
  }

  emitCandidateView(view);
};

export const setDefaultHomepageFamily = (family: TPageFamily | null) => {
  defaultHomepageFamily = family;

  const pending = pendingCandidateViews;
  pendingCandidateViews = [];
  pending.forEach(emitCandidateView);
};

export const markAnalyticsReady = (
  pathname: string,
  track: TTrackEvent
) => {
  if (trackEvent) {
    return;
  }

  trackEvent = track;
  lastPathname = pathname;

  const page = getPageFamily(pathname);
  if (page) {
    recordCandidateView(page);
  }
};

export const notePageNavigation = (pathname: string, action?: string) => {
  const previousPathname = lastPathname;
  lastPathname = pathname;

  if (!trackEvent) {
    return;
  }

  if (action === 'PUSH' || action === 'POP') {
    hasNavigated = true;
  }

  const page = getPageFamily(pathname);
  const previousPage =
    previousPathname === null ? null : getPageFamily(previousPathname);

  if (page && page !== previousPage) {
    recordCandidateView(page);
  }
};

// Module state otherwise lives for the whole page load; logout clears it so
// the next user starts with a fresh landing and default homepage.
export const resetPageEntry = () => {
  trackEvent = null;
  hasNavigated = false;
  lastPathname = null;
  defaultHomepageFamily = undefined;
  pendingCandidateViews = [];
};
